import { isBlockItem, itemDef } from "../game/items";
import type { Atlas } from "../engine/textures";
import { ATLAS_COLS, ATLAS_ROWS, TILE_SIZE } from "../engine/textures";
import { blockDef, tileForFace } from "../engine/blocks";

const cache = new Map<string, string>();

export function iconUrl(id: number, atlas: Atlas): string {
  const key = String(id);
  const hit = cache.get(key);
  if (hit) return hit;
  const url = isBlockItem(id) ? blockIcon(id, atlas) : itemIcon(id);
  cache.set(key, url);
  return url;
}

function blockIcon(id: number, atlas: Atlas): string {
  const c = document.createElement("canvas");
  c.width = 48;
  c.height = 48;
  const ctx = c.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  const tile = tileForFace(id, "py");
  const side = tileForFace(id, "px");
  drawTile(ctx, atlas, tile, 12, 4, 24, 14);
  drawTile(ctx, atlas, side, 12, 18, 24, 22);
  const def = blockDef(id);
  if (def.tintGrass) {
    ctx.globalCompositeOperation = "multiply";
    ctx.fillStyle = "rgb(110,170,70)";
    ctx.fillRect(12, 4, 24, 14);
    ctx.globalCompositeOperation = "source-over";
  }
  return c.toDataURL();
}

function drawTile(
  ctx: CanvasRenderingContext2D,
  atlas: Atlas,
  tile: number,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const col = tile % ATLAS_COLS;
  const row = Math.floor(tile / ATLAS_COLS);
  ctx.drawImage(
    atlas.canvas,
    col * TILE_SIZE,
    row * TILE_SIZE,
    TILE_SIZE,
    TILE_SIZE,
    x,
    y,
    w,
    h,
  );
  void ATLAS_ROWS;
}

function itemIcon(id: number): string {
  const def = itemDef(id);
  const c = document.createElement("canvas");
  c.width = 48;
  c.height = 48;
  const ctx = c.getContext("2d")!;
  const [r, g, b] = def.color;
  const fill = `rgb(${r},${g},${b})`;
  const dark = `rgb(${Math.max(0, r - 50)},${Math.max(0, g - 50)},${Math.max(0, b - 50)})`;
  ctx.clearRect(0, 0, 48, 48);

  if (def.icon === "ingot") {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(8, 20);
    ctx.lineTo(40, 16);
    ctx.lineTo(40, 30);
    ctx.lineTo(8, 34);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = dark;
    ctx.fillRect(8, 30, 32, 4);
  } else if (def.icon === "gem") {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(24, 6);
    ctx.lineTo(40, 20);
    ctx.lineTo(24, 42);
    ctx.lineTo(8, 20);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.4)";
    ctx.beginPath();
    ctx.moveTo(24, 10);
    ctx.lineTo(32, 20);
    ctx.lineTo(24, 22);
    ctx.closePath();
    ctx.fill();
  } else if (def.icon === "stick") {
    ctx.strokeStyle = fill;
    ctx.lineWidth = 6;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(14, 36);
    ctx.lineTo(34, 12);
    ctx.stroke();
  } else if (def.icon === "tool") {
    ctx.strokeStyle = "#8a6230";
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(16, 36);
    ctx.lineTo(30, 16);
    ctx.stroke();
    ctx.fillStyle = fill;
    if (def.tool === "sword") {
      ctx.beginPath();
      ctx.moveTo(26, 8);
      ctx.lineTo(40, 18);
      ctx.lineTo(28, 22);
      ctx.closePath();
      ctx.fill();
    } else if (def.tool === "pickaxe") {
      ctx.fillRect(22, 8, 18, 8);
    } else if (def.tool === "axe") {
      ctx.beginPath();
      ctx.moveTo(24, 10);
      ctx.lineTo(42, 16);
      ctx.lineTo(26, 24);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.fillRect(26, 8, 10, 10);
    }
  } else if (def.icon === "armor") {
    ctx.fillStyle = fill;
    if (def.armorSlot === "head") {
      ctx.fillRect(12, 10, 24, 18);
      ctx.fillRect(12, 26, 8, 6);
      ctx.fillRect(28, 26, 8, 6);
    } else if (def.armorSlot === "chest") {
      ctx.fillRect(10, 12, 28, 26);
      ctx.fillRect(6, 12, 8, 14);
      ctx.fillRect(34, 12, 8, 14);
    } else if (def.armorSlot === "legs") {
      ctx.fillRect(12, 10, 24, 10);
      ctx.fillRect(12, 20, 10, 20);
      ctx.fillRect(26, 20, 10, 20);
    } else {
      ctx.fillRect(10, 26, 12, 10);
      ctx.fillRect(26, 26, 12, 10);
    }
    ctx.strokeStyle = dark;
    ctx.lineWidth = 2;
    ctx.strokeRect(14, 14, 20, 16);
  } else if (def.icon === "food") {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.ellipse(24, 26, 14, 12, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = dark;
    ctx.beginPath();
    ctx.ellipse(24, 22, 8, 5, 0, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.arc(24, 24, 13, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.28)";
    ctx.beginPath();
    ctx.arc(19, 19, 5, 0, Math.PI * 2);
    ctx.fill();
  }
  return c.toDataURL();
}
